import { createStyles } from '@mantine/core';
import type {
	AutomationJobResourceAIOutputResponse,
	AutomationUpdateEntityAIOutputIn,
} from '@repo/api-codegen';
import {
	useApiAutomationGetAiOutputResources,
	useApiUpdateEntityAiOutput,
} from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useAuthUser } from '../../../api';
import type { FetchModelListHook } from '../../../api/factories/types';
import type { AutomationJob } from '../../../api/types/models/automation/automationJob';
import type { IBaseModel } from '../../../api/types/models/baseModel';
import { ANALYTICS_EVENTS } from '../../../utils/constants/analytics';
import { closeAllModals, openModal } from '../../ModalManager';
import { TableV2 } from '../../TableV2';
import { AutomationJobAiOutputModal } from './AutomationJobAiOutputModal';
import { useAiGeneratedResourceColumns } from './hooks';

const useStyles = createStyles(() => ({
	body: {
		height: '640px',
	},
}));

interface AutomationJobDrawerAiOutputTableProps {
	automationJob: AutomationJob;
	handleCompleteAutomationReview: (automationJobId: string) => void;
}

export function AutomationJobDrawerAiOutputTable({
	automationJob,
	handleCompleteAutomationReview,
}: AutomationJobDrawerAiOutputTableProps) {
	const { classes } = useStyles();
	const { user, workspace } = useAuthUser();
	const columns = useAiGeneratedResourceColumns();
	const { mutate: updateAiOutput } = useApiUpdateEntityAiOutput();
	const queryClient = useQueryClient();

	const defaultSearchParams = useMemo(
		() => ({
			automation_job_id: automationJob.id,
		}),
		[automationJob.id]
	);

	const handleOnCancel = useCallback(
		(entityId: string) => {
			// Track individual discard event
			trackEvent(
				ANALYTICS_EVENTS.AI_OUTPUT_INDIVIDUAL_DISCARD,
				{
					automation_id: automationJob.automation?.toString() || '',
					job_id: automationJob.id,
					entity_id: entityId,
				},
				user,
				workspace
			);

			updateAiOutput(
				{
					pathParams: { automationJobId: automationJob.id },
					body: {
						automation_job_id: automationJob.id,
						entity_id: entityId,
						discard_output: true,
					},
				},
				{
					onSuccess: () => {
						// Invalidate the AI output resources query to refresh the table
						queryClient.invalidateQueries({
							queryKey: [
								'automation',
								'automation_job',
								automationJob.id,
								'ai_output_resources',
							],
						});
					},
				}
			);
			closeAllModals();
		},
		[
			automationJob.id,
			automationJob.automation,
			updateAiOutput,
			queryClient,
			user,
			workspace,
		]
	);

	const handleOnSubmit = useCallback(
		(body: AutomationUpdateEntityAIOutputIn) => {
			// Track individual approve event
			trackEvent(
				ANALYTICS_EVENTS.AI_OUTPUT_INDIVIDUAL_APPROVE,
				{
					automation_id: automationJob.automation?.toString() || '',
					job_id: automationJob.id,
					entity_id: body.entity_id,
				},
				user,
				workspace
			);

			updateAiOutput(
				{
					pathParams: { automationJobId: automationJob.id },
					body,
				},
				{
					onSuccess: () => {
						// Invalidate the AI output resources query to refresh the table
						queryClient.invalidateQueries({
							queryKey: [
								'automation',
								'automation_job',
								automationJob.id,
								'ai_output_resources',
							],
						});
					},
				}
			);
			closeAllModals();
		},
		[
			automationJob.id,
			automationJob.automation,
			updateAiOutput,
			queryClient,
			user,
			workspace,
		]
	);

	const handleOnCompleteAutomationReview = useCallback(() => {
		handleCompleteAutomationReview(automationJob.id);
		closeAllModals();
	}, [handleCompleteAutomationReview, automationJob.id]);

	const handleRowClick = useCallback(
		(row: AutomationJobResourceAIOutputResponse, _: unknown, __: unknown) => {
			// Track AI output modal opened event
			trackEvent(
				ANALYTICS_EVENTS.AI_OUTPUT_MODAL_OPENED,
				{
					automation_id: automationJob.automation?.toString() || '',
					job_id: automationJob.id,
					entity_id: row.entity.id,
				},
				user,
				workspace
			);

			openModal({
				title: 'Review update',
				children: (
					<AutomationJobAiOutputModal
						onCancel={handleOnCancel}
						onSubmit={handleOnSubmit}
						automationJob={automationJob}
						aiOutputResponse={row}
						onPrevClick={() => {}}
						onNextClick={() => {}}
						handleCompleteAutomationReview={handleOnCompleteAutomationReview}
					/>
				),
				lockScroll: true,
				size: 640,
				classNames: {
					body: classes.body,
				},
			});
		},
		[
			handleOnCancel,
			handleOnSubmit,
			automationJob,
			handleOnCompleteAutomationReview,
			classes.body,
			user,
			workspace,
		]
	);

	return (
		<TableV2
			columns={columns}
			usePaginationList={
				useApiAutomationGetAiOutputResources as unknown as FetchModelListHook<
					AutomationJobResourceAIOutputResponse & IBaseModel
				>
			}
			usePaginationListOptions={{
				pathParams: { automationJobId: automationJob.id },
			}}
			useCodegenListInterface={true}
			defaultRequiredSearchParams={defaultSearchParams}
			withInteractiveHeader={false}
			withSearch
			withInfiniteScroll={false}
			noRecordsText="No resources found"
			onRowClick={handleRowClick}
		/>
	);
}
